import React, { useState, useMemo, useEffect, useCallback } from "react";
import { colors } from "../../utils/colors";
import type {
  Teacher,
  Student,
  AttendanceStatus,
  ClassInfo,
} from "../../utils/types";
import SlideToSubmit from "../../components/SlideToSubmit";
import MonthCalendar from "../../components/MonthCalendar";
import {
  markClassAttendance,
  getClassAttendance,
  updateClassAttendance,
  getStudentAcademicSummary,
} from "../../Api/attendance";
import type { ClassAttendanceEntry } from "../../Api/attendance";
import {
  getTeacherClasses,
  getStudentsForClassApi,
  getToken,
  type TeacherClassMapping,
} from "../../Api/auth";
import { API_URLS } from "../../Api/urls";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  teacher: Teacher;
  onBack: () => void;
}

type View = "class-select" | "marking" | "summary";

// ─── Extended ClassInfo carries DB ids + supervisor flag ──────────────────────

type ClassInfoWithIds = ClassInfo & {
  classId: number;
  sectionId: number;
  isMyClass?: boolean; // true when this is the supervisor's own assigned class
};

// ─── Supervisor API response shape ────────────────────────────────────────────

interface SupervisorSection {
  id: number;
  class_id: number;
  name: string;
  capacity: number | null;
  is_active: boolean;
}

interface SupervisorClass {
  id: number;
  name: string;
  order: number;
  is_active: boolean;
  sections: SupervisorSection[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().split("T")[0];

const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${MONTHS_EN[d.getMonth()]} ${d.getFullYear()}, ${WEEKDAYS[d.getDay()]}`;
};

const STATUSES: {
  value: NonNullable<AttendanceStatus>;
  label: string;
  short: string;
  color: string;
  bg: string;
  border: string;
  rollBg: string;
  rollColor: string;
}[] = [
  {
    value: "present", label: "Present", short: "P",
    color: "#166534", bg: "#f0fdf4", border: "#86efac",
    rollBg: "#22c55e", rollColor: "#fff",
  },
  {
    value: "absent", label: "Absent", short: "A",
    color: "#92400e", bg: "#fff7ed", border: "#fcd34d",
    rollBg: "#f59e0b", rollColor: "#fff",
  },
  {
    value: "late", label: "Late", short: "L",
    color: "#854d0e", bg: "#fef9c3", border: "#facc15",
    rollBg: "#eab308", rollColor: "#fff",
  },
];

function getStatusCfg(s: AttendanceStatus) {
  return STATUSES.find((x) => x.value === s) ?? null;
}

function mappingToClassInfo(m: TeacherClassMapping): ClassInfoWithIds {
  return {
    id: `cls${m.class_id}_sec${m.section_id}`,
    name: m.section_name
      ? `${m.class_name} – Sec ${m.section_name.toUpperCase()}`
      : m.class_name,
    section: m.section_name,
    studentCount: 0,
    classId: m.class_id,
    sectionId: m.section_id,
    isMyClass: true,
  };
}

/** Flatten supervisor API classes → ClassInfoWithIds[], pinning the
 *  supervisor's own assigned class to the top. */
function flattenSupervisorClasses(
  classes: SupervisorClass[],
  teacher: Teacher,
): ClassInfoWithIds[] {
  const list: ClassInfoWithIds[] = [];

  classes.forEach((cls) => {
    if (!cls.is_active) return;
    cls.sections.forEach((sec) => {
      if (!sec.is_active) return;
      const isMyClass =
        teacher.className?.toLowerCase() === cls.name.toLowerCase() &&
        teacher.sectionName?.toUpperCase() === sec.name.toUpperCase();
      list.push({
        id: `cls${cls.id}_sec${sec.id}`,
        name: `${cls.name} – Sec ${sec.name.toUpperCase()}`,
        section: sec.name,
        studentCount: 0,
        classId: cls.id,
        sectionId: sec.id,
        isMyClass,
      });
    });
  });

  // Sort: supervisor's own class first, then by original order
  list.sort((a, b) => (b.isMyClass ? 1 : 0) - (a.isMyClass ? 1 : 0));
  return list;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatsRow: React.FC<{
  total: number; present: number; absent: number; late: number;
}> = ({ total, present, absent, late }) => (
  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
    <div style={{
      flex: 1, background: colors.primaryBg,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: 12, padding: "10px 8px", textAlign: "center",
    }}>
      <div style={{ fontSize: 20, fontWeight: 900, color: colors.primary }}>{total}</div>
      <div style={{ fontSize: 9, fontWeight: 700, color: colors.textMuted, letterSpacing: "0.5px", marginTop: 2 }}>
        TOTAL
      </div>
    </div>
    {[
      { label: "PRESENT", value: present, color: "#166534", bg: "#f0fdf4", border: "#86efac" },
      { label: "ABSENT",  value: absent,  color: "#92400e", bg: "#fff7ed", border: "#fcd34d" },
      { label: "LATE",    value: late,    color: "#854d0e", bg: "#fef9c3", border: "#facc15" },
    ].map((item) => (
      <div key={item.label} style={{
        flex: 1, background: item.bg,
        border: `1px solid ${item.border}`,
        borderRadius: 12, padding: "10px 8px", textAlign: "center",
      }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: item.color }}>
          {String(item.value).padStart(2, "0")}
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, color: item.color, letterSpacing: "0.5px", marginTop: 2 }}>
          {item.label}
        </div>
      </div>
    ))}
  </div>
);

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div style={{
    fontSize: 12, fontWeight: 800, color: colors.text,
    letterSpacing: "0.6px", margin: "16px 0 8px",
  }}>
    {label}
  </div>
);

const StudentRow: React.FC<{
  student: Student;
  index: number;
  onChange: (id: string, status: AttendanceStatus) => void;
  onRollClick: (student: Student) => void;
}> = ({ student, index, onChange, onRollClick }) => {
  const cfg = getStatusCfg(student.attendance ?? null);
  const isMarked = !!student.attendance;

  return (
    <div style={{
      background: cfg ? cfg.bg : "#fff",
      border: `1px solid ${cfg ? cfg.border : colors.borderLight}`,
      borderRadius: 14, padding: "10px 14px",
      display: "flex", alignItems: "center", gap: 12,
      animation: `slideUp 0.2s ease ${Math.min(index * 0.025, 0.25)}s both`,
      transition: "background 0.2s, border-color 0.2s",
    }}>
      <div onClick={() => onRollClick(student)} style={{
        width: 40, height: 40, borderRadius: "50%",
        background: cfg ? cfg.rollBg : colors.primaryBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 13, fontWeight: 800,
        color: cfg ? cfg.rollColor : colors.primary,
        cursor: "pointer", transition: "background 0.2s",
      }}>
        {student.rollNo}
      </div>
      <div style={{
        flex: 1, fontSize: 14, fontWeight: 600,
        color: cfg ? cfg.color : colors.text,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        transition: "color 0.2s",
      }}>
        {student.name}
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {STATUSES.map((s) => {
          const isActive = student.attendance === s.value;
          return (
            <button key={s.value}
              onClick={() => onChange(student.id, isActive ? null : s.value)}
              style={{
                width: 42, height: 42, borderRadius: 10,
                border: `1.5px solid ${isActive ? s.border : isMarked ? "transparent" : colors.borderLight}`,
                background: isActive ? s.bg : isMarked ? "rgba(255,255,255,0.5)" : "#fff",
                color: isActive ? s.color : isMarked ? "rgba(150,150,150,0.5)" : colors.textLight,
                cursor: "pointer", fontSize: 13, fontWeight: 800, transition: "all 0.12s",
              }}
            >
              {s.short}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SummaryRow: React.FC<{
  student: Student; index: number;
  onRollClick: (student: Student) => void;
}> = ({ student, index, onRollClick }) => {
  const cfg = getStatusCfg(student.attendance ?? null);
  if (!cfg) return null;
  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 14, padding: "10px 14px",
      display: "flex", alignItems: "center", gap: 12,
      animation: `slideUp 0.2s ease ${Math.min(index * 0.025, 0.25)}s both`,
    }}>
      <div onClick={() => onRollClick(student)} style={{
        width: 40, height: 40, borderRadius: "50%",
        background: cfg.rollBg, display: "flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0,
        fontSize: 13, fontWeight: 800, color: cfg.rollColor, cursor: "pointer",
      }}>
        {student.rollNo}
      </div>
      <div style={{
        flex: 1, fontSize: 14, fontWeight: 600, color: cfg.color,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {student.name}
      </div>
      <div style={{
        width: 42, height: 42, borderRadius: 10,
        border: `1.5px solid ${cfg.border}`, background: cfg.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 800, color: cfg.color,
      }}>
        {cfg.short}
      </div>
    </div>
  );
};

// ─── Drawer ───────────────────────────────────────────────────────────────────

interface DrawerStats {
  present: number;
  absent: number;
  late: number;
  month?: string;
  total_marked?: number;
}

const AttendanceDrawer: React.FC<{
  student: Student;
  mode: "marking" | "summary";
  status: AttendanceStatus;
  stats: DrawerStats | null;
  statsLoading: boolean;
  updating: boolean;
  updateError: string;
  onStatusChange: (s: AttendanceStatus) => void;
  onClose: () => void;
  onSave: () => void;
  onUpdate: () => void;
}> = ({
  student, mode, status, stats, statsLoading,
  updating, updateError, onStatusChange, onClose, onSave, onUpdate,
}) => {
  const currentCfg = getStatusCfg(student.attendance ?? null);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)",
        zIndex: 200, animation: "fadeIn 0.2s ease both",
      }} />
      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0,
        background: "#fff", borderRadius: "22px 22px 0 0",
        padding: "0 20px 40px", zIndex: 201,
        animation: "sheetSlideUp 0.3s cubic-bezier(.32,.72,0,1) both",
        maxHeight: "85vh", overflowY: "auto",
      }}>
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: colors.border, margin: "14px auto 20px",
        }} />

        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          background: currentCfg ? currentCfg.bg : colors.primaryBg,
          borderRadius: 14, padding: "14px 16px", marginBottom: 18,
        }}>
          <div style={{
            width: 46, height: 46, borderRadius: "50%",
            background: currentCfg ? currentCfg.rollBg : colors.primaryBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, fontWeight: 800,
            color: currentCfg ? currentCfg.rollColor : colors.primary,
            flexShrink: 0,
          }}>
            {student.rollNo}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 16, fontWeight: 700, color: colors.text,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {student.name}
            </div>
            {student.grNumber && (
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {student.grNumber}
              </div>
            )}
          </div>
        </div>

        {statsLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0", marginBottom: 18 }}>
            <div style={{
              width: 22, height: 22,
              border: `2.5px solid ${colors.borderLight}`,
              borderTopColor: colors.primary,
              borderRadius: "50%", animation: "spin 0.7s linear infinite",
            }} />
          </div>
        ) : stats ? (
          <div style={{
            background: "#f8fafc", borderRadius: 12,
            padding: "14px 16px", marginBottom: 20,
          }}>
            {stats.month && (
              <div style={{
                fontSize: 11, fontWeight: 700, color: colors.textMuted,
                letterSpacing: "0.5px", marginBottom: 10, textAlign: "center",
              }}>
                {stats.month.toUpperCase()}
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              {[
                { label: "Present", value: stats.present, color: "#166534" },
                { label: "Absent",  value: stats.absent,  color: "#92400e" },
                { label: "Late",    value: stats.late,    color: "#854d0e" },
              ].map((item) => (
                <div key={item.label} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: item.color, lineHeight: 1 }}>
                    {String(item.value).padStart(2, "0")}
                  </div>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: item.color,
                    marginTop: 4, letterSpacing: "0.4px",
                  }}>
                    {item.label.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
            {stats.total_marked !== undefined && (
              <div style={{
                marginTop: 10, paddingTop: 10,
                borderTop: `1px solid ${colors.borderLight}`,
                textAlign: "center", fontSize: 12, color: colors.textMuted, fontWeight: 500,
              }}>
                Total days marked:{" "}
                <span style={{ fontWeight: 800, color: colors.text }}>{stats.total_marked}</span>
              </div>
            )}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          {STATUSES.map((s) => {
            const isActive = status === s.value;
            return (
              <button key={s.value}
                onClick={() => onStatusChange(isActive ? null : s.value)}
                style={{
                  flex: 1, padding: "18px 0", borderRadius: 14,
                  border: `2px solid ${isActive ? s.border : colors.border}`,
                  background: isActive ? s.bg : "#fff",
                  fontSize: 18, fontWeight: 900,
                  color: isActive ? s.color : colors.textLight,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {s.short}
              </button>
            );
          })}
        </div>

        {updateError && (
          <div style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
            borderRadius: 10, padding: "10px 14px",
            fontSize: 13, color: colors.danger,
            marginBottom: 12, textAlign: "center",
          }}>
            {updateError}
          </div>
        )}

        {mode === "marking" ? (
          <button onClick={onSave} style={{
            width: "100%", padding: "15px",
            background: colors.primaryDark, color: "#fff",
            border: "none", borderRadius: 14,
            fontSize: 15, fontWeight: 700, cursor: "pointer",
          }}>
            Close
          </button>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{
              flex: 1, padding: "15px", background: "#fff", color: colors.text,
              border: `1.5px solid ${colors.border}`, borderRadius: 14,
              fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}>
              Close
            </button>
            <button onClick={onUpdate} disabled={!status || updating} style={{
              flex: 1, padding: "15px",
              background: !status || updating ? colors.primaryLight : colors.primaryDark,
              color: "#fff", border: "none", borderRadius: 14,
              fontSize: 15, fontWeight: 700,
              cursor: !status || updating ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              {updating ? (
                <>
                  <div style={{
                    width: 14, height: 14,
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff", borderRadius: "50%",
                    animation: "spin 0.7s linear infinite",
                  }} />
                  Updating...
                </>
              ) : "Update"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Page shell ───────────────────────────────────────────────────────────────

const PageShell: React.FC<{
  title: string;
  onBack: () => void;
  onCalendar?: () => void;
  children: React.ReactNode;
}> = ({ title, onBack, onCalendar, children }) => (
  <div style={{
    height: "100dvh", background: "#FDFCFE",
    display: "flex", flexDirection: "column", overflow: "hidden",
    fontFamily: "'Inter', sans-serif", position: "relative",
  }}>
    <div style={{
      padding: "30px 20px 14px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      borderBottom: `1px solid ${colors.borderLight}`,
      background: "#fff", flexShrink: 0, zIndex: 10,
    }}>
      <button onClick={onBack} style={{
        background: "none", border: "none", cursor: "pointer", padding: 0,
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 16, fontWeight: 500, color: colors.text,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        {title}
      </button>
      {onCalendar && (
        <button onClick={onCalendar} style={{
          background: "none", border: "none", cursor: "pointer", padding: 6,
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke={colors.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <rect x="8" y="14" width="2" height="2" rx="0.5" fill={colors.primary} />
          </svg>
        </button>
      )}
    </div>
    {children}
  </div>
);

// ─── Attendance status tag ────────────────────────────────────────────────────

const AttendanceTag: React.FC<{ marked: boolean | undefined }> = ({ marked }) => {
  if (marked === undefined) return null;
  if (marked) {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700,
        background: "#f0fdf4", color: "#16a34a",
        border: "1px solid #86efac",
        padding: "2px 8px", borderRadius: 20,
        display: "inline-flex", alignItems: "center", gap: 3,
        whiteSpace: "nowrap",
      }}>
        ✓ Marked
      </span>
    );
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      background: "#fffbeb", color: "#d97706",
      border: "1px solid #fcd34d",
      padding: "2px 8px", borderRadius: 20,
      whiteSpace: "nowrap",
    }}>
      Pending
    </span>
  );
};

// ─── Supervisor class list item ───────────────────────────────────────────────

const SupervisorClassCard: React.FC<{
  cls: ClassInfoWithIds;
  index: number;
  isMarked: boolean | undefined;
  onSelect: (cls: ClassInfoWithIds) => void;
}> = ({ cls, index, isMarked, onSelect }) => (
  <button onClick={() => onSelect(cls)} style={{
    width: "100%",
    padding: "16px 18px",
    background: cls.isMyClass ? "#EEE3F7" : colors.primaryBg,
    border: `1.5px solid ${cls.isMyClass ? colors.primary : colors.borderLight}`,
    borderRadius: 14, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 14,
    textAlign: "left",
    animation: `slideUp 0.25s ease ${index * 0.03}s both`,
    position: "relative",
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 12,
      background: cls.isMyClass ? "#D8C9F0" : "#EEE3F7",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    </div>

    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: colors.primary, marginBottom: cls.isMyClass ? 4 : 0 }}>
        {cls.name}
      </div>
      {cls.isMyClass && (
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: colors.primary, color: "#fff",
          padding: "2px 8px", borderRadius: 20,
          display: "inline-block",
        }}>
          MY CLASS
        </span>
      )}
    </div>

    <AttendanceTag marked={isMarked} />

    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={colors.textMuted} strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ClassAttendance: React.FC<Props> = ({ teacher, onBack }) => {
  const isSupervisor = teacher.role === "supervisor";

  const [view, setView] = useState<View>("class-select");
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [selectedClass, setSelectedClass] = useState<ClassInfoWithIds | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  const [assignedClasses, setAssignedClasses] = useState<ClassInfoWithIds[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classError, setClassError] = useState("");

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  const [drawerStudent, setDrawerStudent] = useState<Student | null>(null);
  const [drawerStatus, setDrawerStatus] = useState<AttendanceStatus>(null);
  const [drawerStats, setDrawerStats] = useState<DrawerStats | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerUpdating, setDrawerUpdating] = useState(false);
  const [drawerError, setDrawerError] = useState("");

  // ── Attendance marked status per class (for today) ────────────────────────
  const [attendanceMarkedMap, setAttendanceMarkedMap] = useState<Record<string, boolean | undefined>>({});

  // ── Search state (supervisor only) ─────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");

  // ── Check today's attendance status for a set of classes ──────────────────
  const checkAttendanceStatus = useCallback(async (classes: ClassInfoWithIds[]) => {
    const date = todayISO();
    const results = await Promise.allSettled(
      classes.map((cls) =>
        getClassAttendance({ class_id: cls.classId, section_id: cls.sectionId, date }),
      ),
    );
    const map: Record<string, boolean> = {};
    classes.forEach((cls, i) => {
      const r = results[i];
      map[cls.id] = r.status === "fulfilled" && r.value.filter((e) => e.date === date).length > 0;
    });
    setAttendanceMarkedMap((prev) => ({ ...prev, ...map }));
  }, []);

  // ── Filtered class list (supervisor search) ────────────────────────────────
  const filteredClasses = useMemo(() => {
    if (!isSupervisor || !searchQuery.trim()) return assignedClasses;
    const q = searchQuery.toLowerCase();
    return assignedClasses.filter((c) => c.name.toLowerCase().includes(q));
  }, [assignedClasses, searchQuery, isSupervisor]);

  // ── Load attendance for a given date ───────────────────────────────────────
  const loadAttendanceForDate = useCallback(
    async (cls: ClassInfoWithIds, date: string, existingStudents: Student[]) => {
      setView("marking");
      setStudents(existingStudents.map((s) => ({ ...s, attendance: null, attendanceId: undefined })));
      setLoadingAttendance(true);
      try {
        const entries = await getClassAttendance({
          class_id: cls.classId,
          section_id: cls.sectionId,
          date,
        });
        const forDate = entries.filter((e) => e.date === date);
        if (forDate.length > 0) {
          const entryMap: Record<number, ClassAttendanceEntry> = {};
          forDate.forEach((e) => { entryMap[e.student_id] = e; });
          const updated = existingStudents.map((s) => {
            const entry = entryMap[Number(s.id)];
            return entry
              ? { ...s, attendance: entry.status as AttendanceStatus, attendanceId: entry.id }
              : { ...s, attendance: null as AttendanceStatus, attendanceId: undefined };
          });
          setStudents(updated);
          setView("summary");
        } else {
          setStudents(existingStudents.map((s) => ({ ...s, attendance: null, attendanceId: undefined })));
          setView("marking");
        }
      } catch {
        setView("marking");
      }
      setLoadingAttendance(false);
    },
    [],
  );

  // ── Fetch students for a class ──────────────────────────────────────────────
  const fetchStudents = useCallback(
    async (cls: ClassInfoWithIds, date: string) => {
      setLoadingStudents(true);
      setStudentError("");
      try {
        const apiStudents = await getStudentsForClassApi(cls.classId, cls.sectionId);
        const mapped: Student[] = apiStudents
          .filter((s) => s.roll_number != null && String(s.roll_number).trim() !== "")
          .map((s) => ({
            id: String(s.id),
            name: s.full_name,
            rollNo: s.roll_number,
            grNumber: (s as any).enrollment_number ?? (s as any).gr_number ?? undefined,
            attendance: null,
            attendanceId: undefined,
          }))
          .sort((a, b) => {
            const ra = Number(a.rollNo);
            const rb = Number(b.rollNo);
            if (!isNaN(ra) && !isNaN(rb)) return ra - rb;
            return String(a.rollNo).localeCompare(String(b.rollNo));
          });
        await loadAttendanceForDate(cls, date, mapped);
      } catch (err) {
        setStudentError(err instanceof Error ? err.message : "Could not load students.");
        setLoadingStudents(false);
      }
      setLoadingStudents(false);
    },
    [loadAttendanceForDate],
  );

  // ── Bootstrap: load class list ──────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      setLoadingClasses(true);
      setClassError("");
      try {
        if (isSupervisor) {
          // ── Supervisor: fetch all classes from the supervisor endpoint ──
          const token = getToken();
          const response = await fetch(API_URLS.GET_ALL_CLASSE_LIST_FOR_SUPERVISOR, {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const allClasses: SupervisorClass[] = await response.json();
          const classList = flattenSupervisorClasses(allClasses, teacher);
          setAssignedClasses(classList);
          checkAttendanceStatus(classList);
          // Do NOT auto-navigate for supervisor — always show the full list
        } else {
          // ── Class teacher: existing logic ──────────────────────────────
          const mappings = await getTeacherClasses(Number(teacher.id));
          const mine = mappings.filter(
            (m) => m.is_active && m.class_teacher_id === Number(teacher.id),
          );
          const classList = mine.map(mappingToClassInfo);
          setAssignedClasses(classList);
          checkAttendanceStatus(classList);
          if (classList.length === 1) {
            const only = classList[0];
            setSelectedClass(only);
            await fetchStudents(only, todayISO());
          }
        }
      } catch (err) {
        setClassError(err instanceof Error ? err.message : "Could not load classes.");
      }
      setLoadingClasses(false);
    };
    run();
  }, [teacher.id, teacher.role, fetchStudents, isSupervisor, checkAttendanceStatus]);

  // ── Calendar date change ────────────────────────────────────────────────────
  const handleDateChange = async (date: string) => {
    setSelectedDate(date);
    setShowCalendar(false);
    if (!selectedClass) return;
    const bare = students.map((s) => ({
      ...s, attendance: null as AttendanceStatus, attendanceId: undefined,
    }));
    await loadAttendanceForDate(selectedClass, date, bare);
  };

  const handleClassSelect = async (cls: ClassInfoWithIds) => {
    setSelectedClass(cls);
    await fetchStudents(cls, selectedDate);
  };

  const handleStatusChange = (id: string, status: AttendanceStatus) =>
    setStudents((prev) => prev.map((s) => (s.id === id ? { ...s, attendance: status } : s)));

  const handleSubmit = async () => {
    if (!selectedClass) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const entries = students
        .filter((s) => s.attendance)
        .map((s) => ({ student_id: Number(s.id), status: s.attendance! }));
      await markClassAttendance({
        class_id: selectedClass.classId,
        section_id: selectedClass.sectionId,
        date: selectedDate,
        entries,
      });
      if (selectedDate === todayISO()) {
        setAttendanceMarkedMap((prev) => ({ ...prev, [selectedClass.id]: true }));
      }
      await loadAttendanceForDate(selectedClass, selectedDate, students);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to submit. Try again.");
    }
    setSubmitting(false);
  };

  // ── Drawer open/close/save ──────────────────────────────────────────────────
  const openDrawer = async (s: Student) => {
    setDrawerStudent(s);
    setDrawerStatus(s.attendance ?? null);
    setDrawerStats(null);
    setDrawerError("");
    setDrawerLoading(true);
    try {
      const summary = await getStudentAcademicSummary(Number(s.id));
      setDrawerStats({
        present:      summary.present,
        absent:       summary.absent,
        late:         summary.late,
        month:        summary.month,
        total_marked: summary.total_marked,
      });
    } catch {
      setDrawerStats(null);
    }
    setDrawerLoading(false);
  };

  const closeDrawer = () => { setDrawerStudent(null); setDrawerError(""); };

  const saveDrawerLocal = () => {
    if (!drawerStudent) return;
    setStudents((prev) =>
      prev.map((s) => s.id === drawerStudent.id ? { ...s, attendance: drawerStatus } : s),
    );
    closeDrawer();
  };

  const saveDrawerApi = async () => {
    if (!drawerStudent || !drawerStatus || !selectedDate) return;
    setDrawerUpdating(true);
    setDrawerError("");
    try {
      const res = await updateClassAttendance(Number(drawerStudent.id), selectedDate, { status: drawerStatus });
      const updatedEntry = res.data;
      setStudents((prev) =>
        prev.map((s) =>
          s.id === drawerStudent.id
            ? { ...s, attendance: updatedEntry.status as AttendanceStatus, attendanceId: updatedEntry.id }
            : s,
        ),
      );
      closeDrawer();
    } catch (err) {
      setDrawerError(err instanceof Error ? err.message : "Update failed. Try again.");
    }
    setDrawerUpdating(false);
  };

  const counts = useMemo(() => ({
    present:  students.filter((s) => s.attendance === "present").length,
    absent:   students.filter((s) => s.attendance === "absent").length,
    late:     students.filter((s) => s.attendance === "late").length,
    unmarked: students.filter((s) => !s.attendance).length,
  }), [students]);

  const allMarked = students.length > 0 && counts.unmarked === 0;

  const datePillText = selectedClass
    ? `${selectedClass.name} — ${formatDate(selectedDate)}`
    : formatDate(selectedDate);

  // ── Global CSS animations ───────────────────────────────────────────────────
  const globalStyles = `
    @keyframes shimmer {
      0%   { background-position:  200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes sheetSlideUp {
      from { transform: translateY(100%); }
      to   { transform: translateY(0);   }
    }
  `;

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (loadingClasses) {
    return (
      <div style={{
        height: "100dvh", background: "#FDFCFE",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", fontFamily: "'Inter', sans-serif",
      }}>
        <style>{globalStyles}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 36, height: 36,
            border: `3px solid ${colors.borderLight}`,
            borderTopColor: colors.primary, borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
            margin: "0 auto 14px",
          }} />
          <div style={{ fontSize: 14, color: colors.textMuted, fontWeight: 500 }}>
            {isSupervisor ? "Loading all classes…" : "Loading your classes…"}
          </div>
        </div>
      </div>
    );
  }

  // ── Summary view ────────────────────────────────────────────────────────────
  if (view === "summary" && selectedClass) {
    const absentList  = students.filter((s) => s.attendance === "absent");
    const lateList    = students.filter((s) => s.attendance === "late");
    const presentList = students.filter((s) => s.attendance === "present");

    return (
      <PageShell title="Student attendance" onBack={onBack} onCalendar={() => setShowCalendar(true)}>
        <style>{globalStyles}</style>
        {loadingAttendance ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              width: 30, height: 30,
              border: `3px solid ${colors.borderLight}`,
              borderTopColor: colors.primary, borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }} />
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 20px 32px" }}>
            <div style={{ background: colors.primaryDark, borderRadius: 12, padding: "12px 18px", marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{datePillText}</span>
            </div>
            <StatsRow total={students.length} present={counts.present} absent={counts.absent} late={counts.late} />

            {absentList.length > 0 && (
              <>
                <SectionLabel label="ABSENT" />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {absentList.map((s, i) => (
                    <SummaryRow key={s.id} student={s} index={i} onRollClick={openDrawer} />
                  ))}
                </div>
              </>
            )}
            {lateList.length > 0 && (
              <>
                <SectionLabel label="LATE" />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {lateList.map((s, i) => (
                    <SummaryRow key={s.id} student={s} index={i} onRollClick={openDrawer} />
                  ))}
                </div>
              </>
            )}
            {presentList.length > 0 && (
              <>
                <SectionLabel label="PRESENT" />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {presentList.map((s, i) => (
                    <SummaryRow key={s.id} student={s} index={i} onRollClick={openDrawer} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {drawerStudent && (
          <AttendanceDrawer
            student={drawerStudent} mode="summary"
            status={drawerStatus} stats={drawerStats}
            statsLoading={drawerLoading} updating={drawerUpdating}
            updateError={drawerError} onStatusChange={setDrawerStatus}
            onClose={closeDrawer} onSave={closeDrawer} onUpdate={saveDrawerApi}
          />
        )}
        <MonthCalendar
          visible={showCalendar} onClose={() => setShowCalendar(false)}
          selectedDate={selectedDate} onSelectDate={handleDateChange}
        />
      </PageShell>
    );
  }

  // ── Marking view ────────────────────────────────────────────────────────────
  if (view === "marking" && selectedClass) {
    const markingBack = () => {
      if (!isSupervisor && assignedClasses.length === 1) onBack();
      else setView("class-select");
    };

    return (
      <PageShell title="Student attendance" onBack={markingBack} onCalendar={() => setShowCalendar(true)}>
        <style>{globalStyles}</style>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 20px 130px" }}>
          <div style={{ background: colors.primaryDark, borderRadius: 12, padding: "12px 18px", marginBottom: 14 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{datePillText}</span>
          </div>
          <StatsRow total={students.length} present={counts.present} absent={counts.absent} late={counts.late} />

          {loadingStudents || loadingAttendance ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{
                width: 30, height: 30,
                border: `3px solid ${colors.borderLight}`,
                borderTopColor: colors.primary, borderRadius: "50%",
                animation: "spin 0.7s linear infinite", margin: "0 auto 12px",
              }} />
              <div style={{ fontSize: 13, color: colors.textMuted }}>Loading students…</div>
            </div>
          ) : studentError ? (
            <div style={{
              background: "#fff1f2", border: "1px solid #fca5a5",
              borderRadius: 12, padding: "14px 16px",
              fontSize: 13, color: "#991b1b", textAlign: "center",
            }}>
              {studentError}
            </div>
          ) : students.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: colors.textMuted, fontSize: 14 }}>
              No students found for this class.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {students.map((s, i) => (
                <StudentRow key={s.id} student={s} index={i} onChange={handleStatusChange} onRollClick={openDrawer} />
              ))}
            </div>
          )}
        </div>

        {/* Slide-to-submit */}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 20, pointerEvents: "none" }}>
          <div style={{ height: 48, background: "linear-gradient(to bottom, rgba(253,252,254,0), rgba(253,252,254,1))" }} />
          <div style={{ background: "#FDFCFE", padding: "0 20px 32px", pointerEvents: "all" }}>
            {submitError && (
              <div style={{
                background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
                borderRadius: 10, padding: "8px 14px", marginBottom: 10,
                fontSize: 13, color: colors.danger, textAlign: "center",
              }}>
                {submitError}
              </div>
            )}
            <SlideToSubmit
              onConfirm={handleSubmit}
              disabled={submitting || !allMarked || loadingStudents}
              label={allMarked
                ? "Slide to confirm"
                : `${counts.unmarked} student${counts.unmarked !== 1 ? "s" : ""} unmarked`}
              confirmedLabel="Attendance Saved!"
            />
          </div>
        </div>

        {drawerStudent && (
          <AttendanceDrawer
            student={drawerStudent} mode="marking"
            status={drawerStatus} stats={drawerStats}
            statsLoading={drawerLoading} updating={false}
            updateError="" onStatusChange={setDrawerStatus}
            onClose={closeDrawer} onSave={saveDrawerLocal} onUpdate={saveDrawerLocal}
          />
        )}
        <MonthCalendar
          visible={showCalendar} onClose={() => setShowCalendar(false)}
          selectedDate={selectedDate} onSelectDate={handleDateChange}
        />
      </PageShell>
    );
  }

  // ── Class select screen ─────────────────────────────────────────────────────
  return (
    <div style={{
      height: "100dvh", background: "#FDFCFE",
      display: "flex", flexDirection: "column", overflow: "hidden",
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{globalStyles}</style>

      {/* Header */}
      <div style={{
        padding: "30px 20px 14px",
        display: "flex", alignItems: "center", gap: 12,
        borderBottom: `1px solid ${colors.borderLight}`,
        background: "#fff", flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          background: "none", border: "none", cursor: "pointer", padding: 0,
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 16, fontWeight: 500, color: colors.text,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Student attendance
        </button>

        {/* Supervisor badge */}
        {isSupervisor && (
          <span style={{
            marginLeft: "auto",
            fontSize: 10, fontWeight: 700,
            background: "#7C3AED", color: "#fff",
            padding: "3px 10px", borderRadius: 20,
            letterSpacing: "0.4px",
          }}>
            SUPERVISOR
          </span>
        )}
      </div>

      {/* Search bar — supervisor only */}
      {isSupervisor && (
        <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
          <div 
            style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: "#F3F4F6",
                        borderRadius: 12,
                        padding: "10px 14px",
                        border: `1.5px solid ${colors.borderLight}`,
                     
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search class or section…"
              style={{
                flex: 1, background: "none", border: "none", outline: "none",
                fontSize: 14, color: colors.text, fontFamily: "'Inter', sans-serif",
              }}
            />
            {searchQuery ? (
              <button onClick={() => setSearchQuery("")} style={{
                background: "none", border: "none", cursor: "pointer",
                padding: 0, color: colors.textMuted, fontSize: 16, lineHeight: 1,
              }}>
                ×
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Supervisor info strip */}
      {/* {isSupervisor && (
        <div style={{
          margin: "10px 20px 0",
          background: "#F5EEFA",
          borderRadius: 10, padding: "9px 14px",
          display: "flex", alignItems: "center", gap: 8,
          flexShrink: 0,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke={colors.primary} strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontSize: 12, color: colors.primary, fontWeight: 600 }}>
            {filteredClasses.length} class{filteredClasses.length !== 1 ? "es" : ""} shown
            {searchQuery ? ` for "${searchQuery}"` : " · your class pinned to top"}
          </span>
        </div>
      )} */}

      {/* Class list */}
      <div style={{
        flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
        padding: "14px 20px 20px",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {classError && (
          <div style={{
            background: "#fff1f2", border: "1px solid #fca5a5",
            borderRadius: 12, padding: "14px 16px",
            fontSize: 13, color: "#991b1b", textAlign: "center",
          }}>
            {classError}
          </div>
        )}

        {!classError && filteredClasses.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: colors.textMuted, fontSize: 14 }}>
            {searchQuery ? "No classes match your search." : "No classes found."}
          </div>
        )}

        {isSupervisor
          ? filteredClasses.map((cls, i) => (
              <SupervisorClassCard
                key={cls.id} cls={cls} index={i}
                isMarked={attendanceMarkedMap[cls.id]}
                onSelect={handleClassSelect}
              />
            ))
          : assignedClasses.map((cls, i) => (
              <button key={cls.id} onClick={() => handleClassSelect(cls)} style={{
                width: "100%", padding: "16px 18px",
                background: colors.primaryBg,
                border: `1.5px solid ${colors.borderLight}`,
                borderRadius: 14, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 14,
                textAlign: "left",
                animation: `slideUp 0.25s ease ${i * 0.04}s both`,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: "#EEE3F7",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87" />
                    <path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: colors.primary, marginBottom: 4 }}>
                    {cls.name}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: colors.primary, color: "#fff",
                    padding: "2px 8px", borderRadius: 20,
                    display: "inline-block",
                  }}>
                    MY CLASS
                  </span>
                </div>
                <AttendanceTag marked={attendanceMarkedMap[cls.id]} />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
      </div>
    </div>
  );
};

export default ClassAttendance;